import { FC, useState } from 'react';
import {
	Checkbox,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Upload,
	UploadFile,
} from 'antd';
import rule from '@/libs/antd/rules';
import { useForm } from '@/libs/antd/form';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import TextArea from 'antd/es/input/TextArea';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import { NAPBackgroundInfoPayload } from '@/libs/fleet-api/services/nap';
import { FileData } from '@/libs/fleet-api/services/types';
import { useMutation } from '@tanstack/react-query';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { uploader } from '@/libs/fleet-api/uploader';

type ApplicationData =
	NAPBackgroundInfoPayload['specialConditionSubmission'][number] & {
		index: number;
	};

type ModalData =
	| { type: 'new' }
	| {
			type: 'edit';
			data: ApplicationData;
	  };

export const useSpecialApplicationModal = (
	onSubmit: (type: ModalData['type'], data: ApplicationData) => void,
) => {
	const [modalData, setModalData] = useState<ModalData>();

	return {
		data: modalData,
		isOpen: Boolean(modalData),
		open: (data: ModalData) => setModalData(data),
		close: () => setModalData(undefined),
		onSubmit,
	};
};

const SpecialApplicationModal: FC<
	ReturnType<typeof useSpecialApplicationModal> & {
		approverOptions: MasterData[];
		statusOptions: MasterData[];
		subjectOptions: MasterData[];
		prospectId: string;
	}
> = (props) => {
	const { form, fieldName } = useForm<ApplicationData>();
	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<FleetModal
			open={props.isOpen}
			width={940}
			closable={false}
			buttonPosition="end"
			okText="SAVE"
			cancelText="BACK"
			onCancel={() => props.close()}
			onOk={form.submit}
			afterOpenChange={(isOpen) => {
				if (!isOpen) {
					form.resetFields();
				} else if (props.data?.type === 'edit') {
					form.setFieldsValue(props.data.data);
				}
			}}
		>
			<FleetTitle>Informasi Pengajuan Kondisi Khusus</FleetTitle>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={(values) => {
					props.close();
					props.onSubmit(props.data!.type, values);
				}}
			>
				<Form.Item
					name={fieldName('id')}
					hidden
				/>
				<Form.Item
					name={fieldName('index')}
					hidden
				/>
				<Row
					gutter={40}
					style={{ marginBlock: 20 }}
				>
					<Col span={12}>
						<Form.Item
							label="Perihal"
							name={fieldName('subjectId')}
							rules={[rule.required()]}
							labelCol={{ span: 10, style: { alignSelf: 'start' } }}
						>
							<Select
								options={props.subjectOptions.map((data) => {
									return {
										label: data.value,
										value: data.id,
									};
								})}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Ketentuan"
							name={fieldName('provisions')}
							rules={[rule.required()]}
							labelCol={{ span: 10, style: { alignSelf: 'start' } }}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={24}>
						<Form.Item
							label="Usulan"
							name={fieldName('proposal')}
							rules={[rule.required()]}
							labelCol={{ span: 5, style: { alignSelf: 'start' } }}
						>
							<TextArea rows={2} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Form.Item
					label="Pemutus Terakhir"
					name={fieldName('finalApprover')}
					rules={[rule.required()]}
					labelCol={{ span: 24 }}
				>
					<Checkbox.Group>
						<Row gutter={[20, 20]}>
							{props.approverOptions.map((data) => (
								<Col
									key={data.id}
									span={6}
								>
									<Checkbox value={data.id}>{data.value}</Checkbox>
								</Col>
							))}
						</Row>
					</Checkbox.Group>
				</Form.Item>
				<Divider />
				<Row gutter={40}>
					<Col span={24}>
						<Form.Item
							label="Pengajuan Kondisi Khusus"
							name={fieldName('specialCondition')}
							rules={[rule.required()]}
							labelCol={{ span: 5, style: { alignSelf: 'start' } }}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={24}>
						<Form.Item
							name={fieldName('specialConditionStatusId')}
							label="Status Pengajuan Kondisi Khusus"
							labelCol={{ span: 5, style: { alignSelf: 'start' } }}
							rules={[rule.required()]}
						>
							<Select
								options={props.statusOptions.map((data) => {
									return {
										label: data.value,
										value: data.id,
									};
								})}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={24}>
						<Form.Item
							name={fieldName('evidenceFile')}
							label="Upload Foto"
							labelCol={{ span: 5, style: { alignSelf: 'start' } }}
							rules={[rule.required()]}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".jpg,.jpeg,.png"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName('evidenceFile'), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${props.prospectId}/special-condition`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});
										form.setFieldValue(fieldName('evidenceFile'), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName('evidenceFile'), null);
									}
								}}
							>
								{uploadMutation.isPending ? (
									<LoadingOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								) : (
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								)}
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</FleetModal>
	);
};

export default SpecialApplicationModal;
